import React, { useState, useEffect, useContext } from "react"
import { useForm, Controller } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"
import { ModalBtnGroup, ModalMessage } from "../Modal"
import AppContext from "../../shared/AppContext"
import InputMask from "react-input-mask"
import { Mutation } from "react-apollo"
import gql from "graphql-tag"
import "./style.scss"

export function ContactSalesForm(props) {
  return (
    <Mutation mutation={ContactSalesRequest}>
      {(mutate) => {
        return <Form {...props} mutate={mutate} />
      }}
    </Mutation>
  )
}

function Form(props) {
  const [message, setMessage] = useState({ type: "", text: "" })
  const {
    register,
    handleSubmit,
    errors,
    setError,
    clearErrors,
    control,
  } = useForm({
    defaultValues: {
      name: props.viewer.name,
      email: props.viewer.email,
    },
  })
  const {
    closeModal,
    handleServiceSelection,
    selectedServiceTypes,
    mutate,
  } = props
  const { trackEvent } = useContext(AppContext)

  useEffect(() => {
    if (!selectedServiceTypes.length) {
      setError(formInput.SERVICE, {
        type: "checkbox",
        message: "Please select a service",
      })
    } else {
      clearErrors([formInput.SERVICE])
    }
  }, [selectedServiceTypes])

  const onSubmit = (data) => {
    const variables = {
      ...data,
      services: selectedServiceTypes,
    }

    mutate({ variables: variables })
      .then(({ data }) => {
        const { payload, message } = data.contactSalesRequest
        setMessage({
          type: payload,
          text: message,
        })
        if (payload === messageType.SUCCESS) {
          trackEvent("Contact Sales Form Submitted", {
            ...variables,
          })
        }
      })
      .catch((err) => {
        console.log("ContactSalesForm err:", err)
        setMessage({
          type: messageType.ERROR,
          text: "An error occurred while trying to submit the form.",
        })
      })
  }

  const isUpgradeFormOnly = () => {
    const hasUpgradePlan = selectedServiceTypes.includes(
      serviceType.UpgradePlan
    )
    const hasManyServices = selectedServiceTypes.length > 1

    return !hasUpgradePlan || hasManyServices ? false : true
  }

  const renderServices = () => {
    return Object.values(serviceType).map((val, index) => {
      let info = val.split(" ")[0].toLowerCase()
      let isSelected = selectedServiceTypes.includes(val)
      return (
        <button
          key={index}
          className="btn btn-service"
          aria-selected={isSelected ? "true" : "false"}
          onClick={(e) => {
            e.preventDefault()
            handleServiceSelection(val)
          }}
          data-cy={`contact-sales-form-${info}`}
        >
          {val}
        </button>
      )
    })
  }

  if (message.type === "success") {
    return (
      <ModalMessage
        messageType={"success"}
        text={message.text}
        closeModal={closeModal}
      />
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-cy="contact-sales-form">
      <label>
        {isUpgradeFormOnly()
          ? "In order to access all the Top Takeaways and the Updated Outlook, you will need to upgrade your plan."
          : "Give us some information to help us better serve you."}
      </label>
      <section className="form-group">
        <label htmlFor={formInput.NAME}>Name</label>
        <ErrorMessage
          errors={errors}
          name={formInput.NAME}
          as={<span className="form-error-message" />}
        />
        <input
          id={formInput.NAME}
          aria-invalid={errors[formInput.NAME] ? "true" : "false"}
          className={`form-control`}
          type="text"
          placeholder="Full Name"
          name={formInput.NAME}
          ref={register({ required: "Name is required" })}
          data-cy="contact-sales-form-name"
        />
      </section>
      <section className="form-group">
        <label htmlFor={formInput.EMAIL}>Email</label>
        <ErrorMessage
          errors={errors}
          name={formInput.EMAIL}
          render={({ message }) => (
            <span className="form-error-message">{message}</span>
          )}
        />
        <input
          id={formInput.EMAIL}
          aria-invalid={errors[formInput.EMAIL] ? "true" : "false"}
          className={`form-control`}
          type="text"
          placeholder="Email"
          name={formInput.EMAIL}
          ref={register({
            required: "Email is required",
            pattern: { value: /^\S+@\S+$/i, message: "Not a valid email" },
          })}
          data-cy="contact-sales-form-email"
        />
      </section>
      {!isUpgradeFormOnly() && (
        <React.Fragment>
          <section className="form-group">
            <label htmlFor="Phone">
              Phone <i>(optional)</i>
            </label>
            <Controller
              name={formInput.PHONE}
              defaultValue={""}
              control={control}
              render={({ onChange, value }) => (
                <InputMask
                  mask="(999)999-9999"
                  value={value}
                  onChange={onChange}
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      type="tel"
                      className="form-control"
                      disableunderline="true"
                      placeholder="Contact number"
                      data-cy="contact-sales-form-phone"
                    />
                  )}
                </InputMask>
              )}
            />
          </section>

          <section className="form-group">
            <label htmlFor={formInput.COMPANY}>Company Name</label>
            <ErrorMessage
              errors={errors}
              name={formInput.COMPANY}
              as={<span className="form-error-message" />}
            />
            <input
              id={formInput.COMPANY}
              aria-invalid={errors[formInput.COMPANY] ? "true" : "false"}
              className={`form-control`}
              type="tel"
              placeholder="Company Name"
              name={formInput.COMPANY}
              ref={register({ required: true })}
              data-cy="contact-sales-form-company"
            />
          </section>
          <section className="form-group">
            <label>
              What are you looking for? <i>(optional)</i>{" "}
            </label>
            <textarea
              className="form-control "
              name={formInput.DESCRIPTION}
              cols="40"
              rows="3"
              ref={register}
              placeholder="I am looking for someone who can talk about…"
              data-cy="contact-sales-form-description"
            />
          </section>
        </React.Fragment>
      )}

      <section className="sales-form-services">
        <label className="sales-form-services-label">
          What services would you like to get access to?
        </label>
        <ErrorMessage
          errors={errors}
          name={formInput.SERVICE}
          as={<span className="form-error-message" />}
        />
        <div className="sales-form-services-options">{renderServices()}</div>
      </section>
      {(message.type === messageType.ERROR ||
        message.type === messageType.SERVER_ERROR) && (
        <ModalMessage messageType={"error"} text={message.text} alignRight />
      )}
      <ModalBtnGroup alignRight>
        <button
          className="btn btn-cancel"
          type="button"
          onClick={closeModal}
          data-cy="contact-sales-form-cancel"
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          type="submit"
          data-cy="contact-sales-form-submit"
        >
          Submit
        </button>
      </ModalBtnGroup>
    </form>
  )
}

const messageType = {
  SUCCESS: "success",
  REQUIRED: "required",
  ERROR: "error",
  SERVER_ERROR: "internalServerError",
}

export const serviceType = {
  UpgradePlan: "Upgrade Plan",
  TalkToSurveyRespondents: "Talk to Survey Respondents",
  RequestAVendorReport: "Request a Vendor Report",
  ConductAFlashSurvey: "Conduct a Flash Survey",
  AccessRespondentCommentary: "Access Respondent Commentary",
}

export const formInput = {
  EMAIL: "email",
  NAME: "name",
  PHONE: "phone",
  COMPANY: "company",
  DESCRIPTION: "description",
  SERVICE: "service",
}

const ContactSalesRequest = gql`
  mutation ContactSalesRequest(
    $name: String!
    $email: String!
    $phone: String
    $description: String
    $company: String
    $services: [String]!
  ) {
    contactSalesRequest(
      name: $name
      email: $email
      phone: $phone
      description: $description
      company: $company
      services: $services
    ) {
      message
      payload
    }
  }
`
